import React, { useEffect, useMemo, useRef, useState } from 'react';
import logo from '../../assets/logo/logo.png';
import getIcon from '../../utils/Icon';
import overviewEn from './overview.en.json';
import overviewRu from './overview.ru.json';
import faqEn from './faq.en.json';
import faqRu from './faq.ru.json';
import termsEn from './terms.en.json';
import termsRu from './terms.ru.json';
import privacyEn from './privacy.en.json';
import privacyRu from './privacy.ru.json';

const TAB_IDS = ['overview', 'faq', 'terms', 'privacy'];
const OVERVIEW_LANG_KEY = 'overviewLang';

function getInitialOverviewLang() {
    try {
        const saved = localStorage.getItem(OVERVIEW_LANG_KEY);
        if (saved?.startsWith('ru')) return 'ru';
    } catch {
        /* ignore */
    }
    return 'en';
}

function formatPlayers(template, count) {
    return template.replace('{{count}}', count);
}

const COST_PRICES = [
    { count: '200', price: '$44.49' },
    { count: '500', price: '$60.49' },
    { count: '1,000', price: '$88.49' },
    { count: '2,000', price: '$128.49' },
    { count: '5,000', price: '$247.49' },
    { count: '10,000', price: '$444.49' },
];

function AccordionRow({ title, preview, details, expandable }) {
    const [open, setOpen] = useState(false);
    const canToggle = expandable && !!details;

    return (
        <button
            type="button"
            className={`w-full text-left py-3.5 border-b border-[#2A2A2A] ${canToggle ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={() => canToggle && setOpen((v) => !v)}
            disabled={!canToggle}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-white text-[18px] font-bold leading-tight">{title}</div>
                    <div className="mt-1.5 text-[#9A9A9A] text-[15px] font-normal leading-[20px] whitespace-pre-line">
                        {open && details ? details : preview}
                    </div>
                </div>
                {canToggle && (
                    <svg
                        className={`w-4 h-4 mt-1 flex-shrink-0 text-[#A0A0A0] transition-transform ${open ? 'rotate-180' : ''}`}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M6 9l6 6 6-6" />
                    </svg>
                )}
            </div>
        </button>
    );
}

function FaqItem({ n, q, a }) {
    const [open, setOpen] = useState(false);

    return (
        <button
            type="button"
            className="w-full text-left py-3 border-b border-[#2A2A2A] cursor-pointer"
            onClick={() => setOpen((v) => !v)}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-white text-[15px] font-bold leading-snug">
                        {n}. {q}
                    </div>
                    {open && (
                        <div className="mt-2 text-[#9A9A9A] text-[14px] font-normal leading-[20px] whitespace-pre-line">
                            {a}
                        </div>
                    )}
                </div>
                <svg
                    className={`w-4 h-4 mt-1 flex-shrink-0 text-[#A0A0A0] transition-transform ${open ? 'rotate-180' : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </div>
        </button>
    );
}

function TermsSection({ n, title, body, subsections, items }) {
    const [open, setOpen] = useState(false);
    const hasContent = !!(body || (subsections && subsections.length) || (items && items.length));

    return (
        <div className="border-b border-[#2A2A2A]">
            <button
                type="button"
                className="w-full text-left py-3 cursor-pointer"
                onClick={() => hasContent && setOpen((v) => !v)}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="text-white text-[15px] font-bold leading-snug">
                        {n ? `${n}. ${title}` : title}
                    </div>
                    {hasContent && (
                        <svg
                            className={`w-4 h-4 mt-1 flex-shrink-0 text-[#A0A0A0] transition-transform ${open ? 'rotate-180' : ''}`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    )}
                </div>
            </button>
            {open && (
                <div className="pb-3 space-y-3">
                    {body && (
                        <p className="text-[#9A9A9A] text-[14px] font-normal leading-[20px] whitespace-pre-line">
                            {body}
                        </p>
                    )}
                    {items?.map((item) => (
                        <div key={item.title}>
                            <div className="text-white text-[14px] font-semibold mb-1">{item.title}</div>
                            <p className="text-[#9A9A9A] text-[14px] font-normal leading-[20px] whitespace-pre-line">
                                {item.body}
                            </p>
                        </div>
                    ))}
                    {subsections?.map((sub) => (
                        <div key={sub.n}>
                            <div className="text-white text-[14px] font-semibold mb-1">
                                {sub.n}. {sub.title}
                            </div>
                            <p className="text-[#9A9A9A] text-[14px] font-normal leading-[20px] whitespace-pre-line">
                                {sub.body}
                            </p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Overview() {
    const [tab, setTab] = useState('overview');
    const [lang, setLang] = useState(getInitialOverviewLang);
    const scrollRef = useRef(null);
    const copy = lang === 'ru' ? overviewRu : overviewEn;

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [tab]);

    const sections = useMemo(() => {
        const s = copy.sections;
        return [
            {
                id: 'promo',
                title: s.promo.title,
                preview: s.promo.preview,
                expandable: true,
                details: (
                    <div className="space-y-2.5">
                        <p>{s.promo.intro}</p>
                        <p className="text-white/90 font-medium">{s.promo.typesTitle}</p>
                        <ul className="space-y-2 list-none">
                            <li>{s.promo.typeCrystals}</li>
                            <li>{s.promo.typeCc}</li>
                            <li>{s.promo.typeCrypto}</li>
                        </ul>
                        <p className="text-white/90 font-medium">{s.promo.featuresTitle}</p>
                        <ul className="space-y-1.5 list-none">
                            <li>{s.promo.featureCategories}</li>
                            <li>{s.promo.featureDuration}</li>
                            <li>{s.promo.featureWinners}</li>
                        </ul>
                        <p>{s.promo.footer}</p>
                    </div>
                ),
            },
            {
                id: 'prizePool',
                title: s.prizePool.title,
                preview: s.prizePool.preview,
                expandable: false,
            },
            {
                id: 'tasks',
                title: s.tasks.title,
                preview: s.tasks.preview,
                expandable: false,
            },
            {
                id: 'crystals',
                title: s.crystals.title,
                preview: s.crystals.preview,
                expandable: true,
                details: (
                    <div className="space-y-2">
                        <p>{s.crystals.p1}</p>
                        <p>{s.crystals.p2}</p>
                    </div>
                ),
            },
            {
                id: 'guide',
                title: s.guide.title,
                preview: s.guide.preview,
                expandable: true,
                details: (
                    <div className="space-y-2">
                        <p className="text-white/90 font-medium">{s.guide.subtitle}</p>
                        <ol className="space-y-2 list-decimal pl-4">
                            <li>{s.guide.step1}</li>
                            <li>{s.guide.step2}</li>
                            <li>{s.guide.step3}</li>
                            <li>{s.guide.step4}</li>
                            <li>{s.guide.step5}</li>
                            <li>{s.guide.step6}</li>
                        </ol>
                    </div>
                ),
            },
            {
                id: 'promoter',
                title: s.promoter.title,
                preview: s.promoter.preview,
                expandable: true,
                details: <p>{s.promoter.body}</p>,
            },
            {
                id: 'cost',
                title: s.cost.title,
                preview: s.cost.preview,
                expandable: true,
                details: (
                    <div className="space-y-2.5">
                        <p>{s.cost.intro}</p>
                        <p>{s.cost.priceDepends}</p>
                        <p className="text-white/90 font-medium">{s.cost.priceListTitle}</p>
                        <ul className="space-y-1.5 list-none">
                            {COST_PRICES.map((row) => (
                                <li key={row.count} className="flex items-center justify-between gap-3">
                                    <span>· {formatPlayers(s.cost.players, row.count)}</span>
                                    <span className="text-white font-medium tabular-nums">{row.price}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ),
            },
            {
                id: 'balance',
                title: s.balance.title,
                preview: s.balance.preview,
                expandable: true,
                details: <p>{s.balance.body}</p>,
            },
            {
                id: 'oxy',
                title: s.oxy.title,
                preview: s.oxy.preview,
                expandable: false,
            },
        ];
    }, [copy]);

    const faq = lang === 'ru' ? faqRu : faqEn;
    const terms = lang === 'ru' ? termsRu : termsEn;
    const privacy = lang === 'ru' ? privacyRu : privacyEn;

    const handleSupport = () => {
        const webApp = window.Telegram?.WebApp;
        if (webApp?.openTelegramLink) {
            webApp.openTelegramLink('https://t.me/oxycrystals');
        } else {
            window.open('https://t.me/oxycrystals', '_blank');
        }
    };

    const toggleLanguage = () => {
        const next = lang === 'en' ? 'ru' : 'en';
        setLang(next);
        try {
            localStorage.setItem(OVERVIEW_LANG_KEY, next);
        } catch {
            /* ignore */
        }
    };

    return (
        <div className="w-full h-full flex flex-col bg-black pt-8 overflow-hidden">
            <div className="flex-shrink-0 pt-3 pb-2 px-3 border-b border-[#2A2A2A]">
                <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-3 overflow-x-auto hide-scrollbar min-w-0">
                        {TAB_IDS.map((id) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setTab(id)}
                                className={`text-[16px] font-normal whitespace-nowrap transition-colors ${
                                    tab === id ? 'text-white' : 'text-[#9A9A9A]'
                                }`}
                            >
                                {copy.tabs[id]}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={handleSupport}
                            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"
                            aria-label="Support"
                        >
                            <img src={getIcon('support')} alt="" className="w-4 h-4 object-contain" />
                        </button>
                        <button
                            type="button"
                            onClick={toggleLanguage}
                            className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[14px] active:scale-95 transition-transform"
                            title={lang === 'en' ? 'Русский' : 'English'}
                            aria-label="Change language"
                        >
                            {lang === 'en' ? '🇺🇸' : '🇷🇺'}
                        </button>
                    </div>
                </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 pb-8">
                {tab === 'overview' && (
                    <>
                        <div className="flex flex-col items-center pt-6 pb-5">
                            <img src={logo} alt="OxyCrystals" className="w-[72px] h-[72px] object-contain" />
                            <h1 className="mt-3 text-white text-[22px] font-bold leading-tight">
                                {copy.brand.title}
                            </h1>
                            <p className="text-[#9A9A9A] text-[17px] font-normal mt-0.5">
                                {copy.brand.subtitle}
                            </p>
                            <p className="mt-4 text-center text-[#EAEAEA] text-[15px] font-normal leading-[21px] max-w-[340px]">
                                {copy.brand.intro}
                            </p>
                            {/* <div className="mt-3 text-center text-[#A0A0A0] text-[12px] leading-[18px] space-y-0.5">
                                <p>{t('overviewPage.brand.bulletPromo')}</p>
                                <p>{t('overviewPage.brand.bulletGame')}</p>
                            </div> */}
                        </div>

                        <div className="border-t border-[#2A2A2A]">
                            {sections.map((section) => (
                                <AccordionRow key={section.id} {...section} />
                            ))}
                        </div>
                    </>
                )}

                {tab === 'faq' && (
                    <div className="pt-4 pb-2">
                        <h2 className="text-white text-[16px] font-bold mb-4 leading-snug">
                            {faq.title}
                        </h2>
                        <div className="space-y-5">
                            {faq.groups.map((group) => (
                                <div key={group.letter}>
                                    <div className="text-white text-[15px] font-bold mb-1">
                                        {group.letter}. {group.title}
                                    </div>
                                    <div className="border-t border-[#2A2A2A]">
                                        {group.items.map((item) => (
                                            <FaqItem
                                                key={item.n}
                                                n={item.n}
                                                q={item.q}
                                                a={item.a}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {tab === 'terms' && (
                    <div className="pt-4 pb-2">
                        <h2 className="text-white text-[16px] font-bold mb-4 leading-snug">
                            {terms.title}
                        </h2>
                        <div className="border-t border-[#2A2A2A]">
                            <TermsSection
                                title={terms.disclaimer.title}
                                items={terms.disclaimer.items}
                            />
                            {terms.sections.map((section) => (
                                <TermsSection
                                    key={section.n}
                                    n={section.n}
                                    title={section.title}
                                    body={section.body}
                                    subsections={section.subsections}
                                />
                            ))}
                            <TermsSection
                                title={terms.contact.title}
                                body={terms.contact.body}
                            />
                        </div>
                    </div>
                )}

                {tab === 'privacy' && (
                    <div className="pt-4 pb-2">
                        <h2 className="text-white text-[16px] font-bold mb-4 leading-snug">
                            {privacy.title}
                        </h2>
                        <div className="border-t border-[#2A2A2A]">
                            {privacy.sections.map((section) => (
                                <TermsSection
                                    key={section.n}
                                    n={section.n}
                                    title={section.title}
                                    body={section.body}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
